import { useCallback, useEffect, useMemo, useRef } from "react";
import { axBackendInstance } from "./../utils/axios-instances";
import { useAuth } from "./../contexts/auth-provider";
import { failureToast } from "./../utils/toasts";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { REFRESH_ACCESS_TOKEN } from "src/constants/mutations";
import {
  toggle_login_expiry_modal,
  toggle_token_refresh_modal,
} from "src/store/features/auth-slice";
import { useDispatch } from "react-redux";

const ACCESS_DENIED_MESSAGE = "You don't have permission to do that.";

let isInitialized = false; // Singleton flag
let refreshAttempted = false; // Single refresh attempt per "session" to avoid loop

export const useAxiosInterceptor = () => {
  const { token, user, setRefreshtoken, setToken } = useAuth();
  const refreshTokenRef = useRef<string | null>(null);
  refreshTokenRef.current = user?.refreshToken ?? localStorage.getItem("refreshToken");
  const dispatch = useDispatch();

  const callRefreshToken = useCallback(
    async (errorMessage: string): Promise<{ success: boolean; newToken?: string }> => {
      if (refreshAttempted) {
        dispatch(toggle_login_expiry_modal(true));
        return { success: false };
      }
      refreshAttempted = true;
      try {
        const refreshToken = refreshTokenRef.current;
        const {
          data: { data, errors },
        } = (await axBackendInstance.post(GRAPHQL_ENDPOINT as string, {
          query: REFRESH_ACCESS_TOKEN,
          variables: {
            refreshAccessTokenInput: {
              refreshToken,
            },
          },
        })) as any;
        const {
          refreshAccessToken: { refreshToken: newRefreshToken, accessToken },
        } = data || { refreshAccessToken: {} };
        if (errors?.[0]?.message) {
          throw new Error(errors[0].message);
        }
        localStorage.setItem("refreshToken", newRefreshToken);
        localStorage.setItem("token", accessToken);
        setRefreshtoken(newRefreshToken);
        setToken(accessToken);
        refreshAttempted = false; // Allow future 401 to trigger refresh again
        dispatch(toggle_token_refresh_modal(false));
        return { success: true, newToken: accessToken };
      } catch (error) {
        dispatch(toggle_login_expiry_modal(true));
        return { success: false };
      }
    },
    [dispatch, setRefreshtoken, setToken]
  );

  const handle403 = useCallback((config: any) => {
    const suppressToast = config?.meta?.suppressForbiddenToast === true;
    if (!suppressToast) {
      failureToast(ACCESS_DENIED_MESSAGE);
    }
    const isPageRequest = config?.meta?.isPageRequest === true;
    if (isPageRequest && typeof window !== "undefined") {
      window.location.replace("/access-denied");
    }
  }, []);

  const reqResInterceptor = useCallback(
    (config: any) => {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    [token]
  );

  const reqErrInterceptor = async (error: any) => Promise.reject(error);

  const extractQueryFromConfig = (configData: any): string => {
    if (!configData) return "";
    try {
      if (typeof configData === "string") {
        const parsed = JSON.parse(configData);
        return typeof parsed?.query === "string" ? parsed.query : "";
      }
      if (typeof configData === "object") {
        return typeof configData?.query === "string" ? configData.query : "";
      }
    } catch (error) {
      console.warn("Failed to parse GraphQL request payload", error);
    }
    return "";
  };

  const shouldSkipAuthRefresh = (config: any) =>
    config?.meta?.skipAuthRefresh === true;

  const shouldSuppressAuthErrorToast = (config: any) =>
    config?.meta?.suppressAuthErrorToast === true;

  const resResInterceptor = useCallback(
    async (response: any) => {
      const error = response.data?.errors?.[0];
      const config = response.config;
      const errorCode = error?.extensions?.errorCode;

      if (errorCode === 401) {
        if (shouldSkipAuthRefresh(config)) {
          return Promise.reject(response);
        }
        const queryString = extractQueryFromConfig(config?.data);
        const isUpdateStoreUser = queryString.includes("updateStoreUser");
        const result = await callRefreshToken(error?.message ?? "Unauthorized");
        if (result.success && result.newToken && !isUpdateStoreUser) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${result.newToken}`;
          return axBackendInstance.request(config);
        }
        return Promise.reject(response);
      }

      if (errorCode === 403) {
        handle403(config);
        return Promise.reject(response);
      }

      if (error) {
        if (error.message?.includes("Refresh token")) {
          return response;
        }
        const queryString = extractQueryFromConfig(config?.data);
        const shouldSuppressLoggedInUserSalesHistoryError =
          queryString.includes("loggedInUserSalesHistory") &&
          typeof error.message === "string" &&
          error.message.toLowerCase().includes("store");
        if (shouldSuppressLoggedInUserSalesHistoryError) {
          return response;
        }
        if (!shouldSuppressAuthErrorToast(config)) {
          failureToast(error.message);
        }
      }
      return response;
    },
    [callRefreshToken, handle403]
  );

  const resErrInterceptor = useCallback(
    async (error: any) => {
      const status = error.response?.status;
      const config = error.config;
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.detail;

      if (status === 401) {
        if (shouldSkipAuthRefresh(config)) {
          return Promise.reject(error);
        }
        const queryString = extractQueryFromConfig(config?.data);
        const isUpdateStoreUser = queryString.includes("updateStoreUser");
        const result = await callRefreshToken(errorMessage ?? "Unauthorized");
        if (result.success && result.newToken && !isUpdateStoreUser) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${result.newToken}`;
          return axBackendInstance.request(config);
        }
        return Promise.reject(error);
      }

      if (status === 403) {
        handle403(config);
        return Promise.reject(error);
      }

      if (errorMessage?.includes("Refresh token")) {
        return Promise.reject(error);
      }
      if (!shouldSuppressAuthErrorToast(config)) {
        failureToast(errorMessage ?? "Something went wrong");
      }
      return Promise.reject(error);
    },
    [callRefreshToken, handle403]
  );

  useEffect(() => {
    if (!isInitialized) {
      const reqInterceptor = axBackendInstance.interceptors.request.use(
        reqResInterceptor,
        reqErrInterceptor
      );

      const resInterceptor = axBackendInstance.interceptors.response.use(
        resResInterceptor,
        resErrInterceptor
      );

      isInitialized = true;

      return () => {
        axBackendInstance.interceptors.request.eject(reqInterceptor);
        axBackendInstance.interceptors.response.eject(resInterceptor);
        isInitialized = false;
      };
    }
  }, [token, reqResInterceptor, resResInterceptor, resErrInterceptor]);

  return useMemo(() => ({ axBe: axBackendInstance }), []);
};
